import dayjs from 'dayjs'
import { z } from 'zod/v4'

/**
 * Relative date filter wire tokens for list QueryBar filters.
 * Format: `$rel:{amount}:{unit}:{direction}`
 * Example: `$rel:1:weeks:past`
 *
 * Tokens stay relative on the wire and are resolved against `now` at query time.
 */

export const RelativeDateFilterUnitSchema = z.enum(['days', 'weeks', 'months'])
export type RelativeDateFilterUnit = z.infer<
  typeof RelativeDateFilterUnitSchema
>

export const RelativeDateFilterDirectionSchema = z.enum(['past', 'future'])
export type RelativeDateFilterDirection = z.infer<
  typeof RelativeDateFilterDirectionSchema
>

export const RelativeDateFilterValueSchema = z.object({
  amount: z.number().int().positive(),
  unit: RelativeDateFilterUnitSchema,
  direction: RelativeDateFilterDirectionSchema,
})
export type RelativeDateFilterValue = z.infer<
  typeof RelativeDateFilterValueSchema
>

const RELATIVE_DATE_TOKEN_RE =
  /^\$rel:(\d+):(days|weeks|months):(past|future)$/

export function parseRelativeDateFilterValue(
  value: string,
): RelativeDateFilterValue | null {
  const match = RELATIVE_DATE_TOKEN_RE.exec(value)
  if (!match) return null
  const amount = Number(match[1])
  const unit = match[2] as RelativeDateFilterUnit
  const direction = match[3] as RelativeDateFilterDirection
  const parsed = RelativeDateFilterValueSchema.safeParse({
    amount,
    unit,
    direction,
  })
  return parsed.success ? parsed.data : null
}

export function isRelativeDateFilterValue(
  value: unknown,
): value is string {
  return typeof value === 'string' && parseRelativeDateFilterValue(value) !== null
}

export function formatRelativeDateFilterValue(
  value: RelativeDateFilterValue,
): string {
  return `$rel:${value.amount}:${value.unit}:${value.direction}`
}

export function resolveRelativeDateFilterValue(
  value: string | RelativeDateFilterValue,
  now: Date = new Date(),
): Date | null {
  const parsed =
    typeof value === 'string' ? parseRelativeDateFilterValue(value) : value
  if (!parsed) return null

  const base = dayjs(now)
  const unit =
    parsed.unit === 'days'
      ? 'day'
      : parsed.unit === 'weeks'
        ? 'week'
        : 'month'

  if (parsed.direction === 'past') {
    return base.subtract(parsed.amount, unit).toDate()
  }
  return base.add(parsed.amount, unit).toDate()
}

/** Resolve a filter comparison value; leave absolute dates unchanged. */
export function resolveFilterDateComparisonValue(
  value: unknown,
  now: Date = new Date(),
): string {
  if (typeof value !== 'string') return String(value)
  if (!isRelativeDateFilterValue(value)) return value
  const resolved = resolveRelativeDateFilterValue(value, now)
  return resolved ? resolved.toISOString() : value
}

export type RelativeDateFilterPreset = RelativeDateFilterValue & {
  /** Stable id for React keys */
  id: string
}

/** v1 presets: past offsets only */
export const RELATIVE_DATE_FILTER_PRESETS: RelativeDateFilterPreset[] = [
  { id: '1-day', amount: 1, unit: 'days', direction: 'past' },
  { id: '1-week', amount: 1, unit: 'weeks', direction: 'past' },
  { id: '2-weeks', amount: 2, unit: 'weeks', direction: 'past' },
  { id: '1-month', amount: 1, unit: 'months', direction: 'past' },
]

function unitPhrase(
  amount: number,
  unit: RelativeDateFilterUnit,
): string {
  if (unit === 'days') return amount === 1 ? 'a day' : `${amount} days`
  if (unit === 'weeks') return amount === 1 ? 'a week' : `${amount} weeks`
  return amount === 1 ? 'a month' : `${amount} months`
}

/**
 * Human label for a relative token given the comparison op (`lt`/`gt`/…).
 * - after (`gt`/`gte`) + past → "within the last week"
 * - before (`lt`/`lte`) + past → "more than a week ago"
 */
export function formatRelativeDateFilterLabel(
  value: string | RelativeDateFilterValue,
  op: string,
): string | null {
  const parsed =
    typeof value === 'string' ? parseRelativeDateFilterValue(value) : value
  if (!parsed) return null

  const phrase = unitPhrase(parsed.amount, parsed.unit)
  const isAfter = op === 'gt' || op === 'gte' || op === 'after'
  const isBefore = op === 'lt' || op === 'lte' || op === 'before'

  if (parsed.direction === 'past') {
    if (isAfter) return `within the last ${phrase.replace(/^a /, '')}`
    if (isBefore) return `more than ${phrase} ago`
    return `${phrase} ago`.replace(/^a /, '1 ')
  }

  // Future (not used in v1 presets, but supported for labels)
  if (isBefore) return `within the next ${phrase.replace(/^a /, '')}`
  if (isAfter) return `more than ${phrase} from now`
  return `in ${phrase}`
}

export function getRelativeDateFilterPresetsForOp(
  op: string,
): Array<RelativeDateFilterPreset & { label: string; token: string }> {
  return RELATIVE_DATE_FILTER_PRESETS.map((preset) => {
    const token = formatRelativeDateFilterValue(preset)
    const label =
      formatRelativeDateFilterLabel(preset, op) ??
      formatRelativeDateFilterValue(preset)
    return { ...preset, label, token }
  })
}
